import React from 'react';
import type { ProcedureInstance, WorkflowState } from '../../../services/auditService';

/**
 * Portal target for the ResponseShell action cluster. ExecutionModal renders an
 * empty slot in its Modal.Header (next to the close button) and publishes that
 * DOM node here; ResponseShell portals its comm icons + status action button
 * into it. When null (e.g. shell used outside ExecutionModal), the cluster
 * renders inline in the body as before.
 */
export const ExecutionHeaderSlotContext = React.createContext<HTMLElement | null>(null);

/**
 * Unsaved-changes channel. ResponseShell reports whether the body has edits that
 * haven't been persisted (autosave status 'dirty'/'failed'); ExecutionModal reads
 * it to warn before closing. Null when the shell is hosted outside ExecutionModal.
 */
export const ExecutionDirtyContext = React.createContext<((dirty: boolean) => void) | null>(null);

/** Caller-supplied role context for ResponseShell — the user's relationship to this instance. */
export type ResponseRole = 'performer' | 'approver' | 'lead' | 'other';

/** Autosave indicator state machine — used by useAutosave + ResponseShell's status pill. */
export type AutosaveStatus = 'idle' | 'dirty' | 'saving' | 'saved' | 'failed';

/** All input the shell needs to render header + footer + autosave indicator. */
export interface ResponseShellProps {
  instance: ProcedureInstance;
  procedureType: string;
  performerName?: string | null;
  approverName?: string | null;
  role: ResponseRole;
  onSaveDraft?: () => Promise<void>;
  onSubmit?: () => Promise<void>;
  onApprove?: () => Promise<void>;
  onReject?: (comment: string) => Promise<void>;
  onReopen?: () => Promise<void>;
  autosaveStatus?: AutosaveStatus;
  /**
   * G2 (phase 108) — number of response lines carrying a non-empty reject_note.
   * When > 0 the procedure cannot be Approved (the Approve verb is disabled) and
   * a maker-visible "N rejected" badge is shown. Bodies compute this from their
   * rows and report it up. Defaults to 0 (no rejections).
   */
  rejectedCount?: number;
  /**
   * Submit-completeness gate. When a NON-EMPTY string, the maker's
   * "Save & submit for review" action is disabled and this string is shown as
   * the reason (tooltip + inline note in the split menu). Null/undefined => the
   * procedure is complete and Submit is allowed. Each body computes this from
   * its own rows ("every question must be answered before submit" — 2026-06-29).
   * Save-draft is NEVER gated; only the Submit transition is.
   */
  incompleteReason?: string | null;
  children: React.ReactNode;
}

/** Props every procedure_type body component receives from the dispatcher. */
export interface ProcedureResponseProps {
  instance: ProcedureInstance;
  role: ResponseRole;
  performerName?: string | null;
  approverName?: string | null;
  onSaved?: () => void;
  /**
   * True when the instance is Approved — the body MUST disable every editing
   * affordance (answer inputs, per-row EvidenceCell upload, RaiseFindingButton).
   * G3 (phase 108): an approved response is immutable in the UI. Each body may
   * also derive this locally from `instance.workflow_state === 'Approved'`; this
   * optional override exists so the host can force read-only without changing
   * state. NOTE: this is the APPROVED signal, NOT the role-based
   * `visibleFooterButtons().readOnly` (which is also true for the 'other' viewer
   * role and must not be conflated).
   */
  readOnly?: boolean;
}

export interface RoleResolution {
  role: ResponseRole;
}

/**
 * Resolve the user's relationship to a procedure instance:
 *   - GRC Admins always get the 'lead' role (super-user override).
 *   - responsible_user_id match -> performer
 *   - approver_user_id match    -> approver
 *   - accountable_user_id match -> lead (program owner)
 *   - else -> other (read-only view)
 */
export function resolveRole(
  instance: {
    responsible_user_id?: number | null;
    maker_user_id?: number | null;        // present on ProcedureInstanceFull (allocate-task)
    approver_user_id?: number | null;
    accountable_user_id?: number | null;
  },
  currentUserId: number,
  isGrcAdmin = false,
  state?: WorkflowState,
): ResponseRole {
  // Soft SoD (D-08): one user may legitimately hold several positional roles.
  // Mirror the backend _check_verb_auth, which maps BOTH responsible_user_id
  // and maker_user_id to performer.
  const isPerformer =
    instance.responsible_user_id === currentUserId ||
    instance.maker_user_id === currentUserId;
  const isApprover = instance.approver_user_id === currentUserId;
  // isGrcAdmin (currently the ADT_FINDINGS view+modify grant) lets an oversight
  // user act in the lead seat. It is folded into isLead — NOT a blanket early
  // return — so a user who is positionally the maker/approver on this task is
  // not mis-promoted to lead and stripped of their Save/Submit buttons just
  // because they also hold the findings grant (which most makers do).
  const isLead = instance.accountable_user_id === currentUserId || isGrcAdmin;

  // State-aware preference: when the same user holds several roles, hand back the
  // role the CURRENT workflow state needs to act in. Positional performer/approver
  // win over the synthetic lead seat so the assigned maker can still submit.
  if (state) {
    if ((state === 'Submitted' || state === 'InReview') && isApprover) return 'approver';
    if ((state === 'Draft' || state === 'Reopened' || state === 'InProgress') && isPerformer) return 'performer';
    if ((state === 'Approved' || state === 'Rejected') && isLead) return 'lead';
  }

  // Fallback: no state supplied, or the state's primary actor isn't held.
  if (isPerformer) return 'performer';
  if (isApprover) return 'approver';
  if (isLead) return 'lead';
  return 'other';
}

/**
 * Shared read-only rule for every procedure-response body. Answer inputs are
 * editable ONLY by the maker (performer) while the instance is still open
 * (Draft/Reopened/InProgress). Everyone else — checker (approver), program lead
 * ('lead'), other viewers — is read-only, exactly like the Submit button being
 * hidden for them. Once Submitted/Approved, inputs lock for the maker too.
 * Host may still force read-only via the explicit prop.
 */
export function computeResponseReadOnly(
  instance: { workflow_state?: WorkflowState | string | null },
  role: ResponseRole,
  readOnlyProp?: boolean,
): boolean {
  if (readOnlyProp !== undefined) return readOnlyProp;
  const state = instance.workflow_state;
  const stateLocked = state === 'Approved' || state === 'Submitted';
  const roleLocked = role !== 'performer';
  return stateLocked || roleLocked;
}

// After 05c — only Walkthrough sub-types remain deferred (per CONTEXT D2
// Deferred Ideas). Walkthrough, RiskAssessment, and ControlTest are live as
// of plans 05c-05 / 05c-06 / 05c-07. WalkthroughDesign and
// WalkthroughCompliance are sub-types of Walkthrough whose split is
// post-v0.3 and intentionally not part of phase 05c.
export const PROCESS_AUDIT_TYPES_DEFERRED_TO_05C = [
  'WalkthroughDesign',
  'WalkthroughCompliance',
] as const;

export const SAMPLE_BEARING_TYPES = ['Checklist', 'Form', 'Confirmation', 'PhysicalCheck'] as const;
export const SAMPLE_LESS_TYPES = ['Questionnaire', 'Rating'] as const;

export function needsSamples(procedureType: string): boolean {
  return (SAMPLE_BEARING_TYPES as readonly string[]).includes(procedureType);
}

export function isDeferredToPhase05c(procedureType: string): boolean {
  return (PROCESS_AUDIT_TYPES_DEFERRED_TO_05C as readonly string[]).includes(procedureType);
}

/**
 * Footer-button visibility matrix derived from CONTEXT D-01.
 * Pure function — unit-testable, used by ResponseShell.
 *
 * Matrix (role x state):
 *   performer + (Draft|Reopened)  -> Save Draft + Submit for Review
 *   approver  + (Submitted|InReview) -> Send Back + Approve
 *   lead      + (Approved|Rejected) -> Reopen
 *   else                              -> no buttons (read-only)
 */
export function visibleFooterButtons(
  state: WorkflowState,
  role: ResponseRole,
): {
  saveDraft: boolean;
  submit: boolean;
  approve: boolean;
  reject: boolean;
  reopen: boolean;
  readOnly: boolean;
} {
  // InProgress is the D-07 state a task enters after start-work; the performer
  // submits from there (backend rule (InProgress, submit) -> Submitted), so the
  // Submit/Save buttons must remain visible in InProgress as well as Draft/Reopened.
  const performerStates = state === 'Draft' || state === 'Reopened' || state === 'InProgress';
  const approverStates = state === 'Submitted' || state === 'InReview';
  const leadStates = state === 'Approved' || state === 'Rejected';
  const showPerformer = role === 'performer' && performerStates;
  const showApprover = role === 'approver' && approverStates;
  const showLead = role === 'lead' && leadStates;
  return {
    saveDraft: showPerformer,
    submit: showPerformer,
    approve: showApprover,
    reject: showApprover,
    reopen: showLead,
    readOnly: !(showPerformer || showApprover || showLead),
  };
}
